'use client';

import { useState } from 'react';
import Icon from '@/components/ui/AppIcon';

interface MatrixService {
  name: string;
  infrastructure: boolean;
  security: boolean;
  cloud: boolean;
  support247: boolean;
  compliance: boolean;
  consulting: boolean;
}

interface ServiceComparisonMatrixProps {
  services: MatrixService[];
}

export default function ServiceComparisonMatrix({ services }: ServiceComparisonMatrixProps) {
  const [selectedServices, setSelectedServices] = useState<string[]>([]);

  const toggleService = (serviceName: string) => {
    setSelectedServices(prev => 
      prev.includes(serviceName) 
        ? prev.filter(s => s !== serviceName)
        : [...prev, serviceName]
    );
  };

  const capabilities = [
    { key: 'infrastructure', label: 'IT Infrastructure Management' },
    { key: 'security', label: 'Cybersecurity Protection' },
    { key: 'cloud', label: 'Cloud Solutions' },
    { key: 'support247', label: '24/7 Emergency Support' },
    { key: 'compliance', label: 'Compliance Management' },
    { key: 'consulting', label: 'Strategic Consulting' },
  ];

  const filteredServices = selectedServices.length > 0 
    ? services.filter(s => selectedServices.includes(s.name))
    : services;

  return (
    <div className="bg-card rounded-xl shadow-card border border-border overflow-hidden">
      {/* Header */}
      <div className="bg-gradient-to-r from-brand-primary to-brand-secondary p-6">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-xl font-headline font-bold text-white mb-1">
              Service Comparison Matrix
            </h3>
            <p className="text-sm font-body text-white/80">
              Compare capabilities across our service offerings
            </p>
          </div>
          <Icon name="TableCellsIcon" size={32} className="text-white/80" />
        </div>
      </div>

      {/* Filter Pills */}
      <div className="p-4 bg-muted border-b border-border">
        <div className="flex flex-wrap gap-2">
          {services.map((service) => (
            <button
              key={service.name}
              onClick={() => toggleService(service.name)}
              className={`px-4 py-2 rounded-full text-xs font-cta font-semibold transition-all duration-200 ${
                selectedServices.includes(service.name)
                  ? 'bg-brand-primary text-white shadow-card'
                  : 'bg-white text-muted-foreground border border-border hover:border-brand-primary'
              }`}
            >
              {service.name}
            </button>
          ))}
        </div>
      </div>

      {/* Comparison Table */}
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-muted">
              <th className="px-6 py-4 text-left text-sm font-headline font-semibold text-primary">
                Capability
              </th>
              {filteredServices.map((service) => (
                <th key={service.name} className="px-6 py-4 text-center text-sm font-headline font-semibold text-primary">
                  {service.name}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {capabilities.map((capability, index) => (
              <tr 
                key={capability.key}
                className={index % 2 === 0 ? 'bg-white' : 'bg-muted/30'}
              >
                <td className="px-6 py-4 text-sm font-body text-foreground">
                  {capability.label}
                </td>
                {filteredServices.map((service) => (
                  <td key={`${service.name}-${capability.key}`} className="px-6 py-4 text-center">
                    {service[capability.key as keyof MatrixService] ? (
                      <Icon name="CheckCircleIcon" size={24} variant="solid" className="text-success inline-block" />
                    ) : (
                      <Icon name="MinusCircleIcon" size={24} className="text-muted-foreground/40 inline-block" />
                    )}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Footer Note */}
      <div className="p-4 bg-muted border-t border-border">
        <p className="text-xs font-body text-muted-foreground text-center">
          All services can be customized to meet your specific business requirements
        </p>
      </div>
    </div>
  );
}